import { useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './InfoPopover.module.css';

export interface InfoPopoverProps {
  readonly title: string;
  readonly children: ReactNode;
  readonly align?: 'left' | 'right' | 'center';
  readonly variant?: 'light' | 'onDark';
}

export function InfoPopover({
  title,
  children,
  align = 'left',
  variant = 'light',
}: InfoPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const alignClass =
    align === 'right'
      ? styles.popoverRight
      : align === 'center'
        ? styles.popoverCenter
        : styles.popoverLeft;

  return (
    <span className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.triggerBtn} ${
          variant === 'onDark' ? styles.triggerOnDark : ''
        } ${open ? styles.triggerActive : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        aria-label={`Más información: ${title}`}
        title={`Ver ayuda: ${title}`}
      >
        i
      </button>

      {open && (
        <div
          className={`${styles.popoverCard} ${alignClass}`}
          role="dialog"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.popoverHeader}>
            <strong className={styles.popoverTitle}>{title}</strong>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Cerrar información"
            >
              ×
            </button>
          </div>
          <div className={styles.popoverBody}>{children}</div>
        </div>
      )}
    </span>
  );
}
